import React, {ReactNode, useEffect, useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {lastCreatedDocumentId$} from 'models/documents/create';
import {
    filtersChanged,
    filtersReset,
    pageNumberChanged,
    refetchWithSortByDescDate,
    sortingsChanged,
    stateReset,
    tableState$,
} from 'models/documents/moving/grid/table';
import {MovingGridFormattedRow, MovingGridRawRow} from 'types';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (
        data: MovingGridRawRow[] | undefined,
        t: TFunction,
        lastCreatedDocumentId?: string | null,
        updOnly?: boolean,
    ) => MovingGridFormattedRow[];
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    headerControls?: ReactNode;
    headerControlsAlignItems?: string;
}

export const MovingGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
    headerControlsAlignItems,
}: Props) => {
    const {t} = useTranslation();
    const {data, pageNumber, isLoading, filters, sortings} = useStore(tableState$);
    const lastCreatedDocumentId = useStore(lastCreatedDocumentId$);

    useEffect(() => {
        stateReset();
        refetchWithSortByDescDate();
    }, []);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );

    const mappedData = React.useMemo(() => dataMapper(data?.content, t, lastCreatedDocumentId), [
        dataMapper,
        data,
        t,
        lastCreatedDocumentId,
    ]);

    return (
        <Table
            columns={columns}
            data={mappedData}
            hasNext={data?.hasNext}
            pageIndex={pageNumber}
            onPageChange={pageNumberChanged}
            isLoading={isLoading}
            userConfig={{
                gridName: 'movingGrid',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
            headerControls={headerControls}
            exportConfig={exportConfig}
            cellContainerSettings={'width: 100%; height: 60px;'}
            headerControlsAlignItems={headerControlsAlignItems}
        />
    );
};
